import { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Plus, X, Check, ChevronDown, ChevronUp, UserMinus } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { toast } from "../../stores/toastStore";
import { confirm } from "../../components/ConfirmDialog";
import * as svc from "../../services/adminService";

// ── Role display helpers ──────────────────────────────────────────────
const ROLES = ["chat_only", "editor", "dept_admin", "sys_admin"];
const ROLE_LABELS = {
  chat_only:  "Chat only",
  editor:     "Editor",
  dept_admin: "Dept admin",
  sys_admin:  "Sys admin",
};
const ROLE_COLOR = {
  sys_admin:  "var(--color-ink)",
  dept_admin: "var(--color-status-over)",
  editor:     "var(--color-status-under)",
  chat_only:  "var(--color-ink-3)",
};

function RoleBadge({ role }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-sans)", fontSize: "9px", fontWeight: 500,
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: ROLE_COLOR[role] || "var(--color-ink-3)",
      }}
    >
      {ROLE_LABELS[role] || role}
    </span>
  );
}

// ── Inline role select — save button only appears on change ───────────
function DeptRoleSelect({ userId, current, deptId, onSaved }) {
  const [role, setRole] = useState(current);
  const [saving, setSaving] = useState(false);
  const changed = role !== current;

  async function save() {
    setSaving(true);
    try {
      await svc.setMembership(userId, deptId, role);
      onSaved(userId, role);
      toast.success("Role updated");
    } catch (err) {
      setRole(current);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="border-0 border-b border-[var(--color-rule-strong)] bg-transparent py-0.5 pr-1 focus:outline-none focus:border-[var(--color-accent)] transition-colors"
        style={{ fontFamily: "var(--font-sans)", fontSize: "9px", fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: ROLE_COLOR[role] || "inherit", cursor: "pointer" }}
      >
        {ROLES.slice(0, 3).map((r) => (
          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
        ))}
      </select>
      {changed && (
        <button
          onClick={save}
          disabled={saving}
          className="p-1 rounded-sm hover:bg-[var(--color-accent-soft)] transition-colors disabled:opacity-40"
          title="Save role"
        >
          <Check size={12} style={{ color: "var(--color-accent)" }} />
        </button>
      )}
    </div>
  );
}

// ── Memberships panel (expanded) ──────────────────────────────────────
function MembershipsPanel({ userId, deptId: currentDeptId, allDepts, onChanged }) {
  const [memberships, setMemberships] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDeptId, setAddDeptId]   = useState("");
  const [addRole, setAddRole]       = useState("chat_only");
  const [adding, setAdding]         = useState(false);

  useEffect(() => {
    svc.fetchMemberships(userId)
      .then((d) => setMemberships(d.memberships || []))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [userId]);

  async function handleRemove(deptId, deptName) {
    const ok = await confirm({
      title: `Remove from ${deptName}`,
      description: "The user will lose access to this department.",
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await svc.removeMembership(userId, deptId);
      setMemberships((prev) => prev.filter((m) => m.department_id !== deptId));
      toast.success("Membership removed");
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleAdd() {
    if (!addDeptId) return;
    setAdding(true);
    try {
      await svc.setMembership(userId, Number(addDeptId), addRole);
      const dept = allDepts.find((d) => d.id === Number(addDeptId));
      setMemberships((prev) => {
        const filtered = prev.filter((m) => m.department_id !== Number(addDeptId));
        return [...filtered, { department_id: Number(addDeptId), role: addRole, name: dept?.name || "", code: dept?.code || "", bot_id: dept?.bot_id || "" }];
      });
      setAddDeptId("");
      setAddRole("chat_only");
      toast.success("Membership added");
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  }

  const existingDeptIds = new Set((memberships || []).map((m) => m.department_id));
  const availableDepts  = allDepts.filter((d) => !existingDeptIds.has(d.id));

  return (
    <div
      className="border-t border-[var(--color-rule)] px-5 py-4 bg-[var(--color-paper-2)]"
      style={{ gridColumn: "1 / -1" }}
    >
      <div
        className="eyebrow mb-3"
        style={{ fontSize: "9px" }}
      >
        MEMBERSHIPS
      </div>

      {loading ? (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>Loading…</p>
      ) : memberships?.length === 0 ? (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>No department memberships.</p>
      ) : (
        <div className="mb-4 flex flex-col gap-1.5">
          {memberships?.map((m) => (
            <div key={m.department_id} className="flex items-center gap-3">
              <span className="mono flex-1" style={{ fontSize: "var(--text-xs)" }}>{m.name || m.code}</span>
              <RoleBadge role={m.role} />
              <button
                onClick={() => handleRemove(m.department_id, m.name)}
                className="p-1 text-[var(--color-ink-3)] hover:text-[var(--color-accent)] transition-colors"
                title="Remove membership"
              >
                <UserMinus size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {availableDepts.length > 0 && (
        <div className="flex items-end gap-2 pt-3 border-t border-[var(--color-rule)]">
          <div className="field flex-1">
            <label className="field-label" style={{ fontSize: "9px" }}>Add to department</label>
            <select
              className="input"
              value={addDeptId}
              onChange={(e) => setAddDeptId(e.target.value)}
              style={{ fontSize: "var(--text-sm)" }}
            >
              <option value="">Select…</option>
              {availableDepts.map((d) => (
                <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
              ))}
            </select>
          </div>
          <div className="field shrink-0">
            <label className="field-label" style={{ fontSize: "9px" }}>Role</label>
            <select
              className="input"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              style={{ fontSize: "var(--text-sm)" }}
            >
              {ROLES.slice(0, 3).map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-ghost shrink-0"
            onClick={handleAdd}
            disabled={!addDeptId || adding}
          >
            {adding ? "Adding…" : "Add"}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Create user dialog ────────────────────────────────────────────────
function CreateUserDialog({ open, onOpenChange, activeDeptId, departments, onCreated }) {
  const emptyForm = { email: "", password: "", display_name: "", role: "chat_only", department_id: activeDeptId };
  const [form, setForm]     = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm((prev) => ({ ...prev, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { user } = await svc.createUser({ ...form, department_id: Number(form.department_id) });
      toast.success(`User ${form.email} created`);
      onCreated(user);
      onOpenChange(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40" style={{ background: "rgba(10,10,10,0.35)" }} />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 border border-[var(--color-rule)] bg-[var(--color-paper)] p-8 focus:outline-none"
          style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.12)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="display" style={{ fontSize: "var(--text-xl)" }}>
              New user
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1.5 hover:bg-[var(--color-paper-2)] transition-colors">
                <X size={15} style={{ color: "var(--color-ink-3)" }} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="field">
              <label className="field-label">Email *</label>
              <input
                type="email"
                className="input"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label className="field-label">Password *</label>
              <input
                type="password"
                className="input"
                required
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="field">
              <label className="field-label">Display name</label>
              <input
                type="text"
                className="input"
                value={form.display_name}
                onChange={(e) => set("display_name", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="field">
                <label className="field-label">Department</label>
                <select
                  className="input"
                  value={form.department_id}
                  onChange={(e) => set("department_id", e.target.value)}
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name} ({d.code})</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="field-label">Role</label>
                <select
                  className="input"
                  value={form.role}
                  onChange={(e) => set("role", e.target.value)}
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {ROLES.slice(0, 3).map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Dialog.Close asChild>
                <button type="button" className="btn btn-ghost">Cancel</button>
              </Dialog.Close>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Create department dialog (sys_admin) ──────────────────────────────
function CreateDeptDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode]     = useState("");
  const [name, setName]     = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { department } = await svc.createDepartment(code.trim(), name.trim());
      toast.success(`Department '${name}' created`);
      onCreated(department);
      onOpenChange(false);
      setCode(""); setName("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40" style={{ background: "rgba(10,10,10,0.35)" }} />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 border border-[var(--color-rule)] bg-[var(--color-paper)] p-8 focus:outline-none"
          style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.12)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="display" style={{ fontSize: "var(--text-xl)" }}>New department</Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1.5 hover:bg-[var(--color-paper-2)] transition-colors">
                <X size={15} style={{ color: "var(--color-ink-3)" }} />
              </button>
            </Dialog.Close>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="field">
              <label className="field-label">Code *</label>
              <input className="input" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. CS" />
            </div>
            <div className="field">
              <label className="field-label">Name *</label>
              <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Computer Science" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Dialog.Close asChild>
                <button type="button" className="btn btn-ghost">Cancel</button>
              </Dialog.Close>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Creating…" : "Create"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export default function AdminPage() {
  const activeDepartmentId = useAuthStore((s) => s.activeDepartmentId);
  const currentUser        = useAuthStore((s) => s.user);
  const isSysAdmin         = currentUser?.role === "sys_admin";

  const [users, setUsers]           = useState([]);
  const [departments, setDepts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showNewUser, setShowNewUser]   = useState(false);
  const [showNewDept, setShowNewDept]   = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingDept, setEditingDept]   = useState(null); // { id, code, name }

  useEffect(() => {
    Promise.all([
      svc.fetchUsers(activeDepartmentId),
      svc.fetchDepartments(),
    ])
      .then(([ud, dd]) => {
        setUsers(ud.users || []);
        setDepts(dd.departments || []);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [activeDepartmentId]);

  async function handleToggleActive(user) {
    const action = user.is_active ? "Deactivate" : "Activate";
    const ok = await confirm({
      title: `${action} ${user.email}?`,
      description: user.is_active
        ? "The user will be locked out immediately."
        : "The user will regain access to their departments.",
      confirmLabel: action,
      tone: user.is_active ? "danger" : "neutral",
    });
    if (!ok) return;
    try {
      await svc.updateUser(user.id, { is_active: !user.is_active, department_id: activeDepartmentId });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
      toast.success(`User ${action.toLowerCase()}d`);
    } catch (err) {
      toast.error(err.message);
    }
  }

  function handleRoleSaved(userId, newRole) {
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, dept_role: newRole } : u));
  }

  async function saveDeptEdit() {
    if (!editingDept) return;
    const { id, code, name } = editingDept;
    if (!code.trim() || !name.trim()) { toast.error("Code and name are required"); return; }
    try {
      await svc.updateDepartment(id, { code: code.trim(), name: name.trim() });
      setDepts((prev) => prev.map((d) => d.id === id ? { ...d, code: code.trim(), name: name.trim() } : d));
      setEditingDept(null);
      toast.success("Department updated");
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <div className="px-8 lg:px-12 pt-10 pb-16 max-w-5xl">
      {/* Header */}
      <div className="reveal">
        <div className="eyebrow">ADMIN · USERS</div>
      </div>
      <div className="reveal flex items-center justify-between mt-4 mb-8" style={{ animationDelay: "120ms" }}>
        <h1 className="display" style={{ fontSize: "var(--text-3xl)" }}>Users &amp; Departments</h1>
        <button className="btn btn-ghost" onClick={() => setShowNewUser(true)}>
          <Plus size={14} /> New user
        </button>
      </div>

      {/* ── Users table ────────────────────────────────────────────── */}
      <div className="reveal mb-12" style={{ animationDelay: "180ms" }}>
        <div className="eyebrow mb-3" style={{ fontSize: "9px" }}>USERS · THIS DEPARTMENT</div>

        {loading ? (
          <p style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>Loading…</p>
        ) : users.length === 0 ? (
          <div className="border border-[var(--color-rule)] px-6 py-10 text-center" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
            No users in this department.
          </div>
        ) : (
          <div className="border border-[var(--color-rule)] overflow-hidden">
            {/* Column headers */}
            <div
              className="grid border-b border-[var(--color-rule)] bg-[var(--color-paper-2)]"
              style={{ gridTemplateColumns: "1fr 1fr 160px 60px 80px" }}
            >
              {["EMAIL", "DISPLAY NAME", "DEPT ROLE", "ACTIVE", ""].map((h) => (
                <div key={h} className="px-4 py-2.5" style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-ink-3)", fontFamily: "var(--font-sans)" }}>
                  {h}
                </div>
              ))}
            </div>

            {users.map((user) => (
              <div key={user.id}>
                {/* Row */}
                <div
                  className="grid items-center border-b border-[var(--color-rule)] last:border-0 hover:bg-[var(--color-paper-2)] transition-colors"
                  style={{ gridTemplateColumns: "1fr 1fr 160px 60px 80px" }}
                >
                  <div className="px-4 py-3">
                    <div className="mono truncate" style={{ fontSize: "var(--text-sm)" }}>{user.email}</div>
                    {!user.is_active && (
                      <div style={{ fontSize: "9px", color: "var(--color-ink-3)", marginTop: 2 }}>inactive</div>
                    )}
                  </div>
                  <div className="px-4 py-3 truncate" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                    {user.display_name || <span style={{ color: "var(--color-ink-3)", fontStyle: "italic" }}>—</span>}
                  </div>
                  <div className="px-4 py-3">
                    <DeptRoleSelect
                      userId={user.id}
                      current={user.dept_role || user.role}
                      deptId={activeDepartmentId}
                      onSaved={handleRoleSaved}
                    />
                  </div>
                  <div className="px-4 py-3">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: user.is_active ? "var(--color-status-even)" : "var(--color-rule-strong)" }}
                      title={user.is_active ? "Active" : "Inactive"}
                    />
                  </div>
                  <div className="px-3 py-3 flex items-center gap-1">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors"
                      title={user.is_active ? "Deactivate" : "Activate"}
                      style={{ fontSize: "9px", color: "var(--color-ink-3)" }}
                    >
                      {user.is_active ? "Off" : "On"}
                    </button>
                    <button
                      onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                      className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors"
                      title="Memberships"
                    >
                      {expandedUser === user.id
                        ? <ChevronUp size={13} style={{ color: "var(--color-ink-2)" }} />
                        : <ChevronDown size={13} style={{ color: "var(--color-ink-3)" }} />
                      }
                    </button>
                  </div>
                </div>

                {/* Memberships panel */}
                {expandedUser === user.id && (
                  <MembershipsPanel
                    userId={user.id}
                    deptId={activeDepartmentId}
                    allDepts={departments}
                    onChanged={() => svc.fetchUsers(activeDepartmentId).then((d) => setUsers(d.users || []))}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Departments (sys_admin only) ────────────────────────────── */}
      {isSysAdmin && (
        <div className="reveal" style={{ animationDelay: "240ms" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow" style={{ fontSize: "9px" }}>DEPARTMENTS</div>
            <button className="btn btn-ghost" style={{ fontSize: "var(--text-xs)" }} onClick={() => setShowNewDept(true)}>
              <Plus size={13} /> New department
            </button>
          </div>

          <div className="border border-[var(--color-rule)] overflow-hidden">
            <div
              className="grid border-b border-[var(--color-rule)] bg-[var(--color-paper-2)]"
              style={{ gridTemplateColumns: "120px 1fr 1fr 80px" }}
            >
              {["CODE", "NAME", "BOT ID", ""].map((h) => (
                <div key={h} className="px-4 py-2.5" style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-ink-3)", fontFamily: "var(--font-sans)" }}>{h}</div>
              ))}
            </div>

            {departments.map((dept) => (
              <div
                key={dept.id}
                className="grid items-center border-b border-[var(--color-rule)] last:border-0"
                style={{ gridTemplateColumns: "120px 1fr 1fr 80px" }}
              >
                {editingDept?.id === dept.id ? (
                  <>
                    <input
                      className="px-4 py-2.5 border-0 border-b border-[var(--color-rule)] bg-white focus:outline-none focus:border-[var(--color-accent)]"
                      value={editingDept.code}
                      onChange={(e) => setEditingDept((p) => ({ ...p, code: e.target.value }))}
                      style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)" }}
                    />
                    <input
                      className="px-4 py-2.5 border-0 border-b border-[var(--color-rule)] bg-white focus:outline-none focus:border-[var(--color-accent)]"
                      value={editingDept.name}
                      onChange={(e) => setEditingDept((p) => ({ ...p, name: e.target.value }))}
                      style={{ fontSize: "var(--text-sm)" }}
                    />
                    <div className="px-4 py-2.5 mono text-[var(--color-ink-3)]" style={{ fontSize: "var(--text-xs)" }}>{dept.bot_id}</div>
                    <div className="px-3 py-2.5 flex items-center gap-1">
                      <button onClick={saveDeptEdit} className="p-1.5 hover:bg-[var(--color-rule)] rounded-sm transition-colors" title="Save">
                        <Check size={13} style={{ color: "var(--color-accent)" }} />
                      </button>
                      <button onClick={() => setEditingDept(null)} className="p-1.5 hover:bg-[var(--color-rule)] rounded-sm transition-colors" title="Cancel">
                        <X size={13} style={{ color: "var(--color-ink-3)" }} />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-4 py-3 mono" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>{dept.code}</div>
                    <div className="px-4 py-3" style={{ fontSize: "var(--text-sm)" }}>{dept.name}</div>
                    <div className="px-4 py-3 mono truncate" style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}>{dept.bot_id}</div>
                    <div className="px-3 py-3">
                      <button
                        onClick={() => setEditingDept({ id: dept.id, code: dept.code, name: dept.name })}
                        className="p-1.5 hover:bg-[var(--color-rule)] rounded-sm transition-colors"
                        style={{ fontSize: "9px", fontWeight: 500, color: "var(--color-ink-3)", fontFamily: "var(--font-sans)" }}
                      >
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Dialogs ────────────────────────────────────────────────── */}
      <CreateUserDialog
        open={showNewUser}
        onOpenChange={setShowNewUser}
        activeDeptId={activeDepartmentId}
        departments={departments}
        onCreated={(user) => setUsers((prev) => [...prev, user])}
      />
      <CreateDeptDialog
        open={showNewDept}
        onOpenChange={setShowNewDept}
        onCreated={(dept) => setDepts((prev) => [...prev, dept])}
      />
    </div>
  );
}
